// Notifications on the technician's phone without a native app (PRD 108;
// Dave, 2026-10-16: the Greenback Business app is later — the installed field
// page is the app until then).
//
// The field service worker (scope /business/field/) holds a Web Push
// subscription, and the api keeps it as a `web` device token routed as the
// `business` app — the same routing the native app will use, so a field
// notice reaches this page now and the app later with nothing changed on the
// server. Personal-finance notices never come here.
//
//   - ASKED FOR, NEVER ASSUMED: the browser's permission prompt opens only
//     from the person pressing *Turn on notifications* (browsers refuse one
//     that is not), never on load.
//   - iPHONE: Safari gives Web Push only to a page added to the Home Screen
//     (iOS 16.4+), so there the card says how to add it instead.
//   - RE-REGISTERED ON EVERY LAUNCH while permission stands, as the native
//     apps re-register their token: the server's row stays fresh, and a
//     subscription the browser rotated is replaced.
//   - SIGNING OUT unsubscribes and deactivates the row first, so a shared
//     phone stops buzzing for the person who left.

import { apiDelete, apiGet, apiPost } from '../../../utils/apiUtils';
import { APP_VERSION } from '../../../config/version';
import { FIELD_SCOPE } from './fieldWorkerRoutes';

export type FieldPushState =
  | 'unsupported' // no service worker or Push API here (or a development build)
  | 'needs_install' // iPhone/iPad Safari, not opened from the Home Screen
  | 'denied' // the person (or the browser) said no; only the browser's settings undo it
  | 'unavailable' // the server has no push keys for this environment
  | 'off' // may be asked
  | 'on';

const DEVICE_TOKENS = '/api/v1/device_tokens';

const isIos = (): boolean =>
  typeof navigator !== 'undefined' &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

const isStandalone = (): boolean =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)')?.matches === true ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true);

const pushSupported = (): boolean =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window &&
  process.env.NODE_ENV === 'production';

async function fieldRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  const registration = await navigator.serviceWorker.getRegistration(FIELD_SCOPE);
  return registration?.scope.endsWith(FIELD_SCOPE) ? registration : null;
}

/** The PushManager wants the VAPID key as bytes. */
export function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const padded = base64url.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(base64url.length / 4) * 4, '=');
  const raw = atob(padded);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

async function serverKey(): Promise<string | null> {
  // `public_key: null` where the environment has no push keys.
  const response = await apiGet(`${DEVICE_TOKENS}/web_push_key`);
  if (!response.ok) throw new Error(`web_push_key answered ${response.status}`);
  const body = (await response.json()) as { public_key?: string };
  return body.public_key ?? null;
}

async function register(subscription: PushSubscription): Promise<void> {
  const json = subscription.toJSON();
  const response = await apiPost(DEVICE_TOKENS, {
    device_token: {
      token: subscription.endpoint,
      platform: 'web',
      app: 'business',
      app_version: APP_VERSION,
      keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
    },
  });
  if (!response.ok) throw new Error(`device_tokens answered ${response.status}`);
}

/** What the My day card shows. Never prompts. */
export async function fieldPushState(): Promise<FieldPushState> {
  if (isIos() && !isStandalone()) return 'needs_install';
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  const registration = await fieldRegistration();
  if (!registration) return 'unsupported';
  const subscription = await registration.pushManager.getSubscription();
  return subscription && Notification.permission === 'granted' ? 'on' : 'off';
}

/**
 * The person pressed *Turn on notifications*: the browser asks, then the
 * page subscribes with the server's key and registers the subscription.
 */
export async function startFieldPush(): Promise<FieldPushState> {
  if (!pushSupported()) return 'unsupported';
  const permission = await Notification.requestPermission();
  if (permission === 'denied') return 'denied';
  if (permission !== 'granted') return 'off';

  const registration = await fieldRegistration();
  if (!registration) return 'unsupported';
  const key = await serverKey();
  if (!key) return 'unavailable';

  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key) }));
  await register(subscription);
  return 'on';
}

/** On launch, while permission stands: refresh the server's row, as the apps do. */
export async function syncFieldPush(): Promise<void> {
  try {
    if (!pushSupported() || Notification.permission !== 'granted') return;
    const registration = await fieldRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (subscription) await register(subscription);
  } catch {
    // A missed refresh is retried on the next launch; never a broken page.
  }
}

let stopping = false;

/**
 * Signing out: this browser stops getting this person's notices.
 *
 * The browser unsubscribes FIRST, then the server is told. Signing out runs
 * from the global 401 handler too, and a DELETE answered 401 would sign out
 * again: unsubscribed already, that second pass finds nothing to stop.
 */
export async function stopFieldPush(): Promise<void> {
  if (stopping) return;
  stopping = true;
  try {
    if (!pushSupported()) return;
    const registration = await fieldRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) return;
    const endpoint = subscription.endpoint;
    await subscription.unsubscribe();
    await apiDelete(`${DEVICE_TOKENS}/web?token=${encodeURIComponent(endpoint)}`);
  } catch {
    // Signing out never waits on push; a dead subscription is deactivated
    // by the server the first time the push service says it is gone.
  } finally {
    stopping = false;
  }
}
